import { useRef, useEffect, useMemo } from "react";
import * as L from "leaflet";
import { useLayers } from "../../context/LayersContext";
import { MAP_TOOLS, useRulerModalContext } from "../../context/RulerContext";
import { useReportState } from "./hooks/useReportState";
import {
  ReportForm,
  ReportOptions,
  ValidationAlerts,
  ProgressIndicator,
  ActionButtons,
} from "./components";
import { Geometry } from "../../services/api";
import { drawingToGeometry } from "../../utils/geo";
import { resolveMarkingSelectionAction } from "./utils/markingSelection";
import {
  captureMapFittedToPernambuco,
  captureMapFocusedOnDrawing,
} from "./utils/captureComponents";
import { enrichLegendsWithCounts } from "../../services/legendCounts";
import { hydrateWmsLegends } from "../../services/wmsClassLegend";
import { hydrateCategoricalLegends } from "../../services/categoricalClassLegend";
import { generateContextLegendData } from "./model/contextLegends";
import { buildReport, ReportGenerationError } from "./model/buildReport";
import { MARKINGS_PER_SCOPE } from "./hooks/useReportState";
import type { MarkingInput, ReportData } from "./model/types";

const CITY_MARKING_ID = "selected-city-geometry";

interface ReportProps {
  onPreviewReady: (reportData: ReportData) => void;
  selectedDrawingId?: string | null;
  selectedCityGeometry?: Geometry | null;
  selectedCityName?: string | null;
  systemMode?: "sin" | "distribuidora";
  onSelectAreaMarking?: (drawingId: string) => void;
  onSelectCityMarking?: () => void;
  onDeselectMarking?: () => void;
}

function isPolygonal(
  geometry: GeoJSON.Geometry | null | undefined
): geometry is GeoJSON.Polygon | GeoJSON.MultiPolygon {
  return geometry?.type === "Polygon" || geometry?.type === "MultiPolygon";
}

export function Report({
  onPreviewReady,
  selectedDrawingId,
  selectedCityGeometry,
  selectedCityName,
  systemMode,
  onSelectAreaMarking,
  onSelectCityMarking,
  onDeselectMarking,
}: ReportProps) {
  const { layers, layerCqlFilters } = useLayers();
  const { drawings } = useRulerModalContext();
  const { state, actions } = useReportState(selectedDrawingId);
  const { toggleMarking } = actions;
  const autoSelectedIdRef = useRef<string | null>(null);
  const isGeneratingRef = useRef(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const cityName = selectedCityName ?? "Município selecionado";
  const markings = useMemo(
    () => [
      ...drawings
        .filter((d) => d.type === MAP_TOOLS.MEASURE_AREA)
        .map((d) => ({ id: d.id, name: d.name, type: d.type })),
      ...(selectedCityGeometry && selectedCityName
        ? [{ id: CITY_MARKING_ID, name: cityName, type: MAP_TOOLS.CITY }]
        : []),
    ],
    [drawings, selectedCityGeometry, selectedCityName, cityName]
  );

  // O mapa acompanha a Marcação só no relatório de uma área — no comparativo
  // não há "a" área selecionada para destacar.
  const handleToggleMarking = (id: string) => {
    const action = resolveMarkingSelectionAction(id, state.selectedMarkings, CITY_MARKING_ID);
    toggleMarking(id);
    if (state.scope !== "marcacao") return;
    switch (action.kind) {
      case "deselect":
        onDeselectMarking?.();
        return;
      case "select-city":
        onSelectCityMarking?.();
        return;
      case "select-area":
        onSelectAreaMarking?.(action.drawingId);
        return;
    }
  };

  // Pré-seleciona a área ou o município que já está selecionado no mapa.
  useEffect(() => {
    if (!selectedDrawingId && !selectedCityGeometry) {
      autoSelectedIdRef.current = null;
      return;
    }
    const candidate = selectedDrawingId
      ? selectedDrawingId
      : selectedCityName
      ? CITY_MARKING_ID
      : null;
    if (
      candidate &&
      state.selectedMarkings.length === 0 &&
      autoSelectedIdRef.current !== candidate
    ) {
      toggleMarking(candidate);
      autoSelectedIdRef.current = candidate;
    }
  }, [
    selectedDrawingId,
    selectedCityGeometry,
    selectedCityName,
    state.selectedMarkings,
    toggleMarking,
  ]);

  // Não deixa o scroll do formulário rolar o mapa por baixo.
  useEffect(() => {
    const element = modalContentRef.current;
    if (!element) return;
    const stop = (e: Event) => e.stopPropagation();
    element.addEventListener("wheel", stop);
    return () => element.removeEventListener("wheel", stop);
  }, []);

  const resolveMarkings = (): MarkingInput[] =>
    state.selectedMarkings.flatMap((id): MarkingInput[] => {
      if (id === CITY_MARKING_ID) {
        const geometry = selectedCityGeometry as unknown as GeoJSON.Geometry | null;
        if (!isPolygonal(geometry)) return [];
        return [
          {
            id,
            name: cityName,
            kind: "municipio",
            geometry,
            mapLayer: L.geoJSON({ type: "Feature", geometry, properties: {} } as any),
          },
        ];
      }
      const drawing = drawings.find((d) => d.id === id);
      const geometry = drawingToGeometry(drawing) as GeoJSON.Geometry | null;
      if (!drawing || !isPolygonal(geometry)) return [];
      return [{ id, name: drawing.name, kind: "area", geometry, mapLayer: drawing.layer }];
    });

  const captureMap = (marking: MarkingInput | null) => {
    if (!marking) return captureMapFittedToPernambuco(undefined, "mainland");
    // Esconde as outras áreas desenhadas para a captura mostrar só esta.
    const otherLayers = drawings
      .filter(
        (d) => d && d.isVisible && d.id !== marking.id && d.type !== MAP_TOOLS.MEASURE
      )
      .map((d) => d.layer);
    return captureMapFocusedOnDrawing(marking.mapLayer, otherLayers);
  };

  const buildContextLegends = async (geometry: GeoJSON.Geometry | null) =>
    enrichLegendsWithCounts(
      await hydrateCategoricalLegends(
        await hydrateWmsLegends(generateContextLegendData(layers, layerCqlFilters))
      ),
      geometry,
      geometry != null
    );

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    actions.startGeneration();
    try {
      const data = await buildReport(
        {
          projectName: state.projectName,
          projectDescription: state.projectDescription,
          scope: state.scope,
          energies: state.energies,
          level: state.level,
          includeContextLayers: state.includeContextLayers,
          systemMode: systemMode ?? "sin",
        },
        state.scope === "estado" ? [] : resolveMarkings(),
        { captureMap, buildContextLegends, onProgress: actions.setProgress }
      );
      actions.stopGeneration();
      onPreviewReady(data);
    } catch (err) {
      actions.setError(
        err instanceof ReportGenerationError
          ? err.message
          : "Erro ao preparar o relatório. Tente novamente."
      );
    } finally {
      isGeneratingRef.current = false;
    }
  };

  const missingMarkings = Math.max(
    0,
    MARKINGS_PER_SCOPE[state.scope] - state.selectedMarkings.length
  );

  return (
    <div id="report-modal" className="flex flex-col gap-4" ref={modalContentRef}>
      <ValidationAlerts
        scope={state.scope}
        missingMarkings={missingMarkings}
        error={state.error}
        onClearError={() => actions.setError(null)}
        isGenerating={state.isGenerating}
      />

      <ProgressIndicator isGenerating={state.isGenerating} progress={state.progress} />

      <ReportForm
        projectName={state.projectName}
        projectDescription={state.projectDescription}
        onProjectNameChange={actions.setProjectName}
        onProjectDescriptionChange={actions.setProjectDescription}
        disabled={state.isGenerating}
      />

      <ReportOptions
        scope={state.scope}
        energies={state.energies}
        level={state.level}
        includeContextLayers={state.includeContextLayers}
        markings={markings}
        selectedMarkings={state.selectedMarkings}
        onSelectScope={actions.setScope}
        onToggleEnergy={actions.toggleEnergy}
        onSelectLevel={actions.setLevel}
        onToggleContextLayers={actions.toggleContextLayers}
        onToggleMarking={handleToggleMarking}
        disabled={state.isGenerating}
      />

      <ActionButtons
        isGenerating={state.isGenerating}
        canGenerate={missingMarkings === 0}
        projectName={state.projectName}
        onGenerate={handleGenerate}
      />
    </div>
  );
}
